import type { Uuid } from "spacetimedb";
import { useMutation } from "@tanstack/react-query";
import { FileTextIcon, MailIcon, XIcon } from "lucide-react";
import { useId, useRef, useState } from "react";

import { Button } from "@/web/components/primitives/button";
import {
  Drawer,
  DrawerBody,
  DrawerCloseIcon,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/web/components/primitives/drawer";
import { Label } from "@/web/components/primitives/label";
import { Spinner } from "@/web/components/primitives/spinner";
import { toaster } from "@/web/components/primitives/toast";
import { ErrorHandler } from "@/web/lib/errors";
import { useProcedureMutation } from "@/web/lib/tanstack";
import { procedures } from "@tycho/web-bindings";

const PDF_CONTENT_TYPE = "application/pdf";

type UploadLeaseDocDrawerProps = {
  /** Underwritable entity that will own the resulting lease document rows. */
  underwritableEntity: Uuid;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after at least one lease doc finishes uploading. */
  onUploaded?: () => void;
};

export function UploadLeaseDocDrawer({
  underwritableEntity,
  open,
  onOpenChange,
  onUploaded,
}: UploadLeaseDocDrawerProps) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>Upload lease documents</DrawerTitle>
          <DrawerCloseIcon />
        </DrawerHeader>
        {/*
          vaul portals DrawerContent on open/close, so this body remounts
          each session — no need to manually reset staged files.
        */}
        <UploadLeaseDocDrawerBody
          underwritableEntity={underwritableEntity}
          onComplete={() => {
            onOpenChange(false);
            onUploaded?.();
          }}
        />
      </DrawerContent>
    </Drawer>
  );
}

function UploadLeaseDocDrawerBody({
  underwritableEntity,
  onComplete,
}: {
  underwritableEntity: Uuid;
  onComplete: () => void;
}) {
  const [files, setFiles] = useState<File[]>([]);
  // Set once the user picks "Submit via email instead" and the request
  // succeeds. We don't persist across drawer open/close — vaul remounts the
  // body and the user can switch back to direct upload at any time, even if
  // they previously requested an email (per the framework's parallel-paths
  // guarantee for lease uploads).
  const [emailRequestSentTo, setEmailRequestSentTo] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const fileInputId = useId();

  const { mutateAsync: createUploadUrl } = useProcedureMutation(procedures.createLeaseDocUploadUrl);
  const { mutateAsync: submitLeaseDoc } = useProcedureMutation(procedures.submitLeaseDoc);
  const requestViaEmail = useProcedureMutation(procedures.requestLeaseDocsViaEmail, {
    onSuccess: ({ sentToEmail }) => {
      setEmailRequestSentTo(sentToEmail);
    },
    onError: ErrorHandler.default,
  });

  const upload = useMutation({
    mutationFn: async () => {
      if (files.length === 0) throw new Error("Please choose at least one lease PDF.");

      // Upload + submit sequentially. On failure we stop the loop and surface
      // the error; already-submitted files stay registered server-side, so a
      // retry only re-uploads the remaining items the user hasn't cleared.
      for (const file of files) {
        const { uploadUrl, objectPath, contentType, contentLengthRange } = await createUploadUrl({
          underwritableEntity,
          contentType: file.type || PDF_CONTENT_TYPE,
        });

        // Headers MUST match the signed URL exactly — same constraint as the
        // tax return / hero image flows.
        const putRes = await fetch(uploadUrl, {
          method: "PUT",
          headers: {
            "content-type": contentType,
            "x-goog-content-length-range": contentLengthRange,
          },
          body: file,
        });
        if (!putRes.ok) {
          const text = await putRes.text().catch((e: unknown) => {
            ErrorHandler.reportError(e);
            return "";
          });
          throw new Error(`Upload to GCS failed (${putRes.status}): ${text}`);
        }

        await submitLeaseDoc({
          underwritableEntity,
          objectPath,
          originalFileName: file.name,
        });
      }
      return { uploaded: files.length };
    },
    onSuccess: ({ uploaded }) => {
      toaster({
        message:
          uploaded === 1
            ? "Lease uploaded — we'll parse it shortly."
            : `${uploaded} leases uploaded — we'll parse them shortly.`,
        variant: "success",
      });
      onComplete();
    },
    onError: ErrorHandler.default,
  });

  const isPending = upload.isPending;
  const canSubmit = files.length > 0 && !isPending;
  const isRequestingEmail = requestViaEmail.isPending;

  if (emailRequestSentTo !== null) {
    return (
      <>
        <DrawerBody className="flex flex-col gap-4">
          <div className="flex items-center gap-3 border border-withus-dark-blue/20 bg-card p-4">
            <MailIcon className="size-6 shrink-0 text-withus-red" aria-hidden />
            <div className="flex flex-col gap-1 text-sm">
              <p className="font-medium">Check your inbox at {emailRequestSentTo}</p>
              <p className="text-muted-foreground">
                Reply to that message with your lease PDFs attached. We'll process them
                automatically — usually within a few minutes of your reply.
              </p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Changed your mind? You can still upload files directly from this device.
          </p>
          <Button
            type="button"
            variant="outline"
            color="red"
            size="md"
            onClick={() => setEmailRequestSentTo(null)}
          >
            Upload from this device instead
          </Button>
        </DrawerBody>
        <DrawerFooter>
          <Button color="red" size="lg" onClick={onComplete}>
            Done
          </Button>
        </DrawerFooter>
      </>
    );
  }

  return (
    <>
      <DrawerBody className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Label htmlFor={fileInputId}>Lease documents</Label>
          <input
            ref={fileInputRef}
            id={fileInputId}
            type="file"
            accept={PDF_CONTENT_TYPE}
            multiple
            className="sr-only"
            disabled={isPending}
            onChange={(e) => {
              const picked = Array.from(e.target.files ?? []);
              setFiles((prev) => [...prev, ...picked]);
              // Reset so picking the same filename again still fires onChange.
              e.target.value = "";
            }}
          />
          <Button
            type="button"
            variant="outline"
            color="red"
            size="md"
            disabled={isPending}
            onClick={() => fileInputRef.current?.click()}
          >
            {files.length === 0 ? "Choose PDF(s)" : "Add more PDFs"}
          </Button>
        </div>
        {files.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {files.map((file, idx) => (
              <li
                key={`${file.name}-${file.lastModified}-${idx}`}
                className="flex items-center gap-2 border border-withus-dark-blue/20 bg-card px-3 py-2 text-sm"
              >
                <FileTextIcon className="size-4 shrink-0 text-withus-dark-blue/60" aria-hidden />
                <span className="flex-1 truncate" title={file.name}>
                  {file.name}
                </span>
                <button
                  type="button"
                  disabled={isPending}
                  onClick={() => setFiles((prev) => prev.filter((_, i) => i !== idx))}
                  aria-label={`Remove ${file.name}`}
                  className="inline-flex size-6 items-center justify-center text-withus-dark-blue/60 hover:text-withus-dark-blue disabled:opacity-50"
                >
                  <XIcon className="size-4" />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {isPending ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner size="medium" />
            <span>Uploading…</span>
          </div>
        ) : null}
      </DrawerBody>
      <DrawerFooter className="flex-col gap-2">
        <Button color="red" size="lg" disabled={!canSubmit} onClick={() => upload.mutate()}>
          {isPending ? "Uploading…" : files.length > 1 ? `Upload ${files.length} files` : "Upload"}
        </Button>
        <Button
          variant="link"
          size="sm"
          disabled={isPending || isRequestingEmail}
          isLoading={isRequestingEmail}
          onClick={() => requestViaEmail.mutate({ underwritableEntity })}
        >
          {isRequestingEmail ? "Sending email…" : "Submit via email instead"}
        </Button>
      </DrawerFooter>
    </>
  );
}
